import Prism from 'prismjs/components/prism-core';
import { $, $$, on, doc } from './util.js';
import { readSettings, writeSettings, SETTINGS_KEY } from './settings.js';

/* API EXAMPLE: one request in five languages (components/hub-api-example.html)
   --------------------------------------------------------------------------
   The template renders the request as raw HTTP, and its responses, so the
   example reads without JavaScript. It also prints the request as JSON in
   <script type="application/json" class="api-ex-spec">. From that this module
   builds cURL, PHP (Guzzle), Python (requests) and JavaScript (fetch), adds the
   language and response switches and a copy button on every panel. Nothing is
   stored per language in the CMS. The template prints only the HTTP tab; each
   other tab is added here with its panel, so a client that runs no JavaScript
   (an AI agent reading the HTML) meets no tab without code behind it.
   A language is one entry in LANGS and one generator in `gen`.

   The language is one choice for every example on every page and in other
   open tabs: the `apiLanguage` property of the shared settings entry
   (settings.js), as the Smarty / Twig switch keeps `templateEngine`.

   Spec: { method, host, path, query: [[name, value]], headers: [[name, value]],
   auth: 'bearer' | 'session' | 'none', bodyType: 'none' | 'json' | 'form' |
   'multipart' | 'properties', body, fields: [[name, value]] }. A GET with a
   'properties' body is the platform's selector-in-the-body form
   (X-Body-Has-Parameters: 1); fetch cannot send a GET body, so the JavaScript
   version moves the selector into the query string and says why. */

/* key, tab label, Prism language; http is rendered by the template */
var LANGS = [
	['http', 'HTTP', 'http'],
	['curl', 'cURL', 'bash'],
	['php', 'PHP', 'php'],
	['python', 'Python', 'python'],
	['js', 'JavaScript', 'javascript']
];
var LANG_KEYS = LANGS.map(function (l) { return l[0]; });
var TOKEN = 'YOUR_ACCESS_TOKEN';

function sq(s) { return "'" + String(s).replace(/'/g, "'\\''") + "'"; }
function phpStr(s) { return "'" + String(s).replace(/\\/g, '\\\\').replace(/'/g, "\\'") + "'"; }
function jsonStr(s) { return JSON.stringify(String(s)); }
function pad(n) { return new Array(n + 1).join(' '); }
function pairs(list) { return Array.isArray(list) ? list.filter(function (kv) { return kv && kv.length === 2; }) : []; }
function toObject(list) { return list.reduce(function (o, kv) { o[kv[0]] = kv[1]; return o; }, {}); }

function jsonBody(spec) {
	if (spec.bodyType !== 'json' || !spec.body) { return null; }
	try { return JSON.parse(spec.body); } catch (e) { return null; }
}

/* Credentials and content type, as the HTTP panel shows them. */
function headersOf(spec) {
	var h = [];
	if (spec.auth === 'bearer') { h.push(['Authorization', 'Bearer ' + TOKEN]); }
	if (spec.auth === 'session') { h.push(['Cookie', 'YOUR_SESSION_COOKIE'], ['X-Csrf-Token', 'YOUR_CSRF_TOKEN']); }
	h.push(['Accept', 'application/json']);
	if (spec.bodyType === 'json') { h.push(['Content-Type', 'application/json']); }
	if (spec.bodyType === 'properties') { h.push(['Content-Type', 'text/plain'], ['X-Body-Has-Parameters', '1']); }
	return h.concat(pairs(spec.headers));
}

/* The libraries set these themselves from the body they are given. */
function libraryHeaders(spec) {
	return headersOf(spec).filter(function (x) {
		return x[0] !== 'Content-Type' || spec.bodyType === 'properties';
	});
}

function toPhp(v, ind) {
	if (v === null) { return 'null'; }
	if (typeof v === 'boolean') { return v ? 'true' : 'false'; }
	if (typeof v === 'number') { return String(v); }
	if (typeof v === 'string') { return phpStr(v); }
	var inner = pad(ind + 4), close = pad(ind);
	if (Array.isArray(v)) {
		if (!v.length) { return '[]'; }
		return '[\n' + v.map(function (x) { return inner + toPhp(x, ind + 4) + ','; }).join('\n') + '\n' + close + ']';
	}
	var keys = Object.keys(v);
	if (!keys.length) { return '[]'; }
	return '[\n' + keys.map(function (k) { return inner + phpStr(k) + ' => ' + toPhp(v[k], ind + 4) + ','; }).join('\n') + '\n' + close + ']';
}

function toPy(v, ind) {
	if (v === null) { return 'None'; }
	if (typeof v === 'boolean') { return v ? 'True' : 'False'; }
	if (typeof v === 'number') { return String(v); }
	if (typeof v === 'string') { return jsonStr(v); }
	var inner = pad(ind + 4), close = pad(ind);
	if (Array.isArray(v)) {
		if (!v.length) { return '[]'; }
		return '[\n' + v.map(function (x) { return inner + toPy(x, ind + 4) + ','; }).join('\n') + '\n' + close + ']';
	}
	var keys = Object.keys(v);
	if (!keys.length) { return '{}'; }
	return '{\n' + keys.map(function (k) { return inner + jsonStr(k) + ': ' + toPy(v[k], ind + 4) + ','; }).join('\n') + '\n' + close + '}';
}

var gen = {};

gen.curl = function (spec) {
	var url = spec.host + spec.path, lines = [], q = pairs(spec.query);
	if (spec.method === 'GET' && spec.bodyType !== 'properties') {
		lines.push('curl -G ' + sq(url));
		q.forEach(function (kv) { lines.push('  --data-urlencode ' + sq(kv[0] + '=' + kv[1])); });
	} else {
		if (q.length) { url += '?' + q.map(function (kv) { return encodeURIComponent(kv[0]) + '=' + encodeURIComponent(kv[1]); }).join('&'); }
		lines.push('curl -X ' + spec.method + ' ' + sq(url));
	}
	headersOf(spec).forEach(function (h) {
		if (spec.bodyType === 'multipart' && h[0] === 'Content-Type') { return; }
		lines.push('  -H ' + sq(h[0] + ': ' + h[1]));
	});
	if ((spec.bodyType === 'json' || spec.bodyType === 'properties') && spec.body) { lines.push('  --data ' + sq(spec.body)); }
	if (spec.bodyType === 'form') { pairs(spec.fields).forEach(function (kv) { lines.push('  --data-urlencode ' + sq(kv[0] + '=' + kv[1])); }); }
	if (spec.bodyType === 'multipart') { pairs(spec.fields).forEach(function (kv) { lines.push('  -F ' + sq(kv[0] + '=' + kv[1])); }); }
	return { code: lines.join(' \\\n') };
};

gen.php = function (spec) {
	var opts = [], body = jsonBody(spec), q = pairs(spec.query), f = pairs(spec.fields);
	opts.push("    'headers' => " + toPhp(toObject(libraryHeaders(spec)), 4) + ',');
	if (q.length) { opts.push("    'query' => " + toPhp(toObject(q), 4) + ','); }
	if (body !== null) { opts.push("    'json' => " + toPhp(body, 4) + ','); }
	if (spec.bodyType === 'properties' && spec.body) { opts.push("    'body' => " + phpStr(spec.body) + ','); }
	if (spec.bodyType === 'form' && f.length) { opts.push("    'form_params' => " + toPhp(toObject(f), 4) + ','); }
	if (spec.bodyType === 'multipart' && f.length) {
		opts.push("    'multipart' => " + toPhp(f.map(function (kv) { return { name: kv[0], contents: kv[1] }; }), 4) + ',');
	}
	return { code: [
		'<?php',
		"require 'vendor/autoload.php';",
		'',
		"$client = new GuzzleHttp\\Client(['base_uri' => " + phpStr(spec.host) + ']);',
		'',
		'$response = $client->request(' + phpStr(spec.method) + ', ' + phpStr(spec.path) + ', [',
		opts.join('\n'),
		']);',
		'',
		'$data = json_decode((string) $response->getBody(), true);'
	].join('\n') };
};

gen.python = function (spec) {
	var args = [], body = jsonBody(spec), q = pairs(spec.query), f = pairs(spec.fields);
	args.push('    ' + jsonStr(spec.host + spec.path) + ',');
	args.push('    headers=' + toPy(toObject(libraryHeaders(spec)), 4) + ',');
	if (q.length) { args.push('    params=' + toPy(toObject(q), 4) + ','); }
	if (body !== null) { args.push('    json=' + toPy(body, 4) + ','); }
	if (spec.bodyType === 'properties' && spec.body) { args.push('    data=' + jsonStr(spec.body) + ','); }
	if (spec.bodyType === 'form' && f.length) { args.push('    data=' + toPy(toObject(f), 4) + ','); }
	if (spec.bodyType === 'multipart' && f.length) {
		args.push('    files=' + toPy(f.reduce(function (o, kv) { o[kv[0]] = [null, kv[1]]; return o; }, {}), 4) + ',');
	}
	return { code: ['import requests', '', 'response = requests.' + spec.method.toLowerCase() + '(', args.join('\n'), ')', 'data = response.json()'].join('\n') };
};

gen.js = function (spec) {
	var q = pairs(spec.query).slice(), out = [], body = jsonBody(spec), moved = false, f = pairs(spec.fields);
	if (spec.bodyType === 'properties' && spec.body) { q.push(['properties', spec.body]); moved = spec.method === 'GET'; }
	out.push('const url = new URL(' + jsonStr(spec.host + spec.path) + ');');
	if (q.length) {
		out.push('url.search = new URLSearchParams({');
		q.forEach(function (kv) { out.push('  ' + jsonStr(kv[0]) + ': ' + jsonStr(kv[1]) + ','); });
		out.push('});');
	}
	if (spec.bodyType === 'form' || spec.bodyType === 'multipart') {
		out.push('');
		out.push('const body = new ' + (spec.bodyType === 'form' ? 'URLSearchParams' : 'FormData') + '();');
		f.forEach(function (kv) { out.push('body.append(' + jsonStr(kv[0]) + ', ' + jsonStr(kv[1]) + ');'); });
	}
	out.push('');
	out.push('const response = await fetch(url, {');
	if (spec.method !== 'GET') { out.push('  method: ' + jsonStr(spec.method) + ','); }
	if (spec.auth === 'session') { out.push("  credentials: 'same-origin',"); }
	out.push('  headers: {');
	headersOf(spec).forEach(function (h) {
		if (h[0] === 'Cookie') { return; }
		if (h[0] === 'Content-Type' && (spec.bodyType === 'form' || spec.bodyType === 'multipart')) { return; }
		if (moved && (h[0] === 'Content-Type' || h[0] === 'X-Body-Has-Parameters')) { return; }
		out.push('    ' + jsonStr(h[0]) + ': ' + jsonStr(h[1]) + ',');
	});
	out.push('  },');
	if (body !== null) { out.push('  body: JSON.stringify(' + JSON.stringify(body, null, 2).replace(/\n/g, '\n  ') + '),'); }
	if (spec.bodyType === 'form' || spec.bodyType === 'multipart') { out.push('  body,'); }
	out.push('});');
	out.push('const data = await response.json();');
	return {
		code: out.join('\n'),
		note: moved ? 'Browsers cannot send a body with a GET request, so this version puts the selector in the query string.' : ''
	};
};

function highlight(code) {
	try { Prism.highlightElement(code); } catch (e) {}
}

function selectText(node) {
	var range = doc.createRange();
	range.selectNodeContents(node);
	var selection = window.getSelection();
	selection.removeAllRanges();
	selection.addRange(range);
}

function addCopy(pre) {
	var button = doc.createElement('button');
	button.type = 'button';
	button.className = 'api-copy';
	button.textContent = 'Copy';
	on(button, 'click', function () {
		var code = $('code', pre) || pre;
		var done = function () {
			button.textContent = 'Copied';
			setTimeout(function () { button.textContent = 'Copy'; }, 1400);
		};
		if (navigator.clipboard && navigator.clipboard.writeText) {
			navigator.clipboard.writeText(code.textContent).then(done, function () { selectText(code); });
		} else {
			selectText(code);
		}
	});
	pre.parentNode.insertBefore(button, pre);
}

/* The Copy button sits at the top right of its panel, over the code. A panel
   long enough to scroll has its vertical scrollbar there, so the button moves
   left by the scrollbar's width instead of covering it. Only panels in view
   are measured: a hidden one has no width, and is placed when it is shown. */
function placeCopies(root) {
	$$('.api-copy', root).forEach(function (button) {
		var pre = button.nextElementSibling;
		if (!pre || !pre.offsetWidth) { return; }
		var style = window.getComputedStyle(pre);
		var bar = pre.offsetWidth - pre.clientWidth - parseFloat(style.borderLeftWidth || 0) - parseFloat(style.borderRightWidth || 0);
		button.style.right = (10 + Math.max(0, bar)) + 'px';
	});
}

function savedLang() {
	var lang = readSettings().apiLanguage;
	return LANG_KEYS.indexOf(lang) === -1 ? 'http' : lang;
}

function showLang(want) {
	$$('[data-api-example].is-ready').forEach(function (ex) {
		// An example whose generator failed for this language falls back to HTTP
		var lang = $('.api-panel[data-lang="' + want + '"]', ex) ? want : 'http';
		$$('.api-sw:not(.api-res-sw) .api-seg', ex).forEach(function (tab) {
			var selected = tab.getAttribute('data-lang') === lang;
			tab.setAttribute('aria-selected', selected ? 'true' : 'false');
			tab.tabIndex = selected ? 0 : -1;
		});
		$$('.api-panel', ex).forEach(function (panel) {
			panel.classList.toggle('is-on', panel.getAttribute('data-lang') === lang);
		});
		var note = $('.api-ex-note', ex);
		if (note) { note.hidden = !(lang === 'js' && note.textContent); }
		placeCopies(ex);
	});
}

/* Arrow keys move along a tablist, as in the other switches on the hub. */
function arrowKeys(tabs, choose) {
	tabs.forEach(function (tab, i) {
		on(tab, 'keydown', function (e) {
			var step = e.key === 'ArrowRight' ? 1 : (e.key === 'ArrowLeft' ? -1 : 0);
			if (!step) { return; }
			e.preventDefault();
			var next = tabs[(i + step + tabs.length) % tabs.length];
			next.focus();
			choose(next);
		});
	});
}

function initOne(ex) {
	var specNode = $('script.api-ex-spec', ex);
	var httpPanel = $('.api-panel[data-lang="http"]', ex);
	if (!specNode || !httpPanel) { return; }
	var spec;
	try { spec = JSON.parse(specNode.textContent); } catch (e) { return; }

	var after = httpPanel, noteText = '';
	var tablist = $('.api-sw:not(.api-res-sw)', ex);
	LANGS.forEach(function (lang) {
		var key = lang[0];
		if (!gen[key]) { return; }
		var out;
		try { out = gen[key](spec); } catch (e) { return; }
		if (out.note) { noteText = out.note; }
		var panel = doc.createElement('div');
		panel.className = 'api-panel';
		panel.setAttribute('data-lang', key);
		var pre = doc.createElement('pre');
		var code = doc.createElement('code');
		code.className = 'language-' + lang[2];
		code.textContent = out.code;
		pre.appendChild(code);
		panel.appendChild(pre);
		after.parentNode.insertBefore(panel, after.nextSibling);
		after = panel;
		highlight(code);
		if (tablist) {
			var tab = doc.createElement('button');
			tab.type = 'button';
			tab.className = 'api-seg';
			tab.setAttribute('role', 'tab');
			tab.setAttribute('data-lang', key);
			tab.setAttribute('aria-selected', 'false');
			tab.tabIndex = -1;
			var dot = doc.createElement('span');
			dot.className = 'api-dot';
			dot.setAttribute('aria-hidden', 'true');
			tab.appendChild(dot);
			tab.appendChild(doc.createTextNode(lang[1]));
			tablist.appendChild(tab);
		}
	});
	var note = doc.createElement('p');
	note.className = 'api-ex-note';
	note.textContent = noteText;
	note.hidden = true;
	after.parentNode.insertBefore(note, after.nextSibling);
	$$('pre', ex).forEach(addCopy);

	var langTabs = $$('.api-sw:not(.api-res-sw) .api-seg', ex);
	function chooseLang(tab) {
		var lang = tab.getAttribute('data-lang');
		writeSettings({ apiLanguage: lang });
		showLang(lang);
	}
	langTabs.forEach(function (tab) { on(tab, 'click', function () { chooseLang(tab); }); });
	arrowKeys(langTabs, chooseLang);

	var resTabs = $$('.api-res-sw .api-seg', ex);
	function chooseRes(tab) {
		resTabs.forEach(function (other) {
			var selected = other === tab;
			other.setAttribute('aria-selected', selected ? 'true' : 'false');
			other.tabIndex = selected ? 0 : -1;
		});
		$$('.api-res', ex).forEach(function (res) {
			res.classList.toggle('is-on', res.getAttribute('data-res') === tab.getAttribute('data-res'));
		});
		placeCopies(ex);
	}
	resTabs.forEach(function (tab) { on(tab, 'click', function () { chooseRes(tab); }); });
	arrowKeys(resTabs, chooseRes);

	ex.classList.add('is-ready');
}

export function initApiExample() {
	var examples = $$('[data-api-example]');
	if (!examples.length) { return; }
	examples.forEach(function (ex) {
		try { initOne(ex); } catch (e) {
			if (window.console && window.console.error) { window.console.error('hub: api example failed', e); }
		}
	});
	showLang(savedLang());

	// A narrower window can give a panel a scrollbar it did not have.
	var resizing = false;
	on(window, 'resize', function () {
		if (resizing) { return; }
		resizing = true;
		window.requestAnimationFrame(function () { resizing = false; placeCopies(doc); });
	});

	// Another tab changed the language: follow it, without writing it back.
	on(window, 'storage', function (e) {
		if (e.key && e.key !== SETTINGS_KEY) { return; }
		showLang(savedLang());
	});
}
